import React, { useState, useEffect } from 'react';
import {
  AlertCircle,
  Calendar,
  DollarSign,
  Eye,
  Plus,
  RefreshCw,
  Send,
  Settings,
  Star,
  TrendingUp,
  Wrench
} from 'lucide-react';
import toast from 'react-hot-toast';
import { jobAPI } from '../../../services/api';
import { suggestionLine } from '../parts/diagnosisEstimate';
import EnhancedPartsSection from '../parts/EnhancedPartsSection';
import DiagnosisFallbackNotice, { isFallbackDiagnosis } from '../parts/DiagnosisFallbackNotice';
import { Modal } from '../../common/Modal';
import CorvinArt from '../../brand/CorvinArt';
import CorvinThinking from '../../corvin/CorvinThinking';

// ENHANCED DIAGNOSIS MODAL
//
// Hoisted verbatim out of JobManagement's function body (Task 6). It was
// redefined on every render there, remounting all seven pieces of its own
// state and re-running its showModal/selectedJob effect.
//
// Closed-over parent-scope values: none. Its interface was already its five
// props (showModal, onClose, diagnosisResult, selectedJob, onCreateEstimate);
// everything else it touches is a module-level import (jobAPI, the lucide
// icons, and EnhancedPartsSection, itself hoisted in this same task).
//
// Converted to the shared Modal primitive (Task 7). isOpen is hardcoded
// true: this component's own early return (`!showModal || !diagnosisResult
// || !selectedJob`) is what controls mounting and protects the body's
// dereferences of diagnosisResult/selectedJob, so it stays. title is the
// plain heading text rather than the old composed <h3> node, because
// Modal renders title inside its own <h3> and nesting another <h3> inside
// it would be invalid; the icon, job number, vehicle, model version and
// timestamp that used to live in that header block are preserved as the
// first element of the body instead, so no information is lost.
  const EnhancedDiagnosisModal = ({
    showModal, 
    onClose, 
    diagnosisResult, 
    selectedJob,
    onCreateEstimate 
  }) => {
    // One estimate list: ticked parts (reported by EnhancedPartsSection) plus
    // suggested repairs/maintenance added with "Add to Job", keyed by row.
    const [pickedParts, setPickedParts] = useState([]);
    const [pickedSuggestions, setPickedSuggestions] = useState({});
    const [openDetails, setOpenDetails] = useState({});
    const [suggestedRepairs, setSuggestedRepairs] = useState([]);
    const [loadingRepairs, setLoadingRepairs] = useState(false);
    const [suggestedMaintenance, setSuggestedMaintenance] = useState([]);
    const [loadingMaintenance, setLoadingMaintenance] = useState(false);
    const [activeDiagnosticTab, setActiveDiagnosticTab] = useState('diagnosis');
    const [vehicleDataLoaded, setVehicleDataLoaded] = useState(false);
    
    // Auto-load vehicle data when modal opens
    useEffect(() => {
      if (showModal && selectedJob && !vehicleDataLoaded) {
        const loadVehicleData = async () => {
          // Load repairs
          try {
            setLoadingRepairs(true);
            const repairsData = await jobAPI.getSuggestedRepairs(selectedJob.id);
            setSuggestedRepairs(repairsData.suggested_repairs || []);
          } catch (error) {
            console.log('No repairs data available:', error.message);
          } finally {
            setLoadingRepairs(false);
          }
          
          // Load maintenance
          try {
            setLoadingMaintenance(true);
            const maintenanceData = await jobAPI.getSuggestedMaintenance(selectedJob.id);
            setSuggestedMaintenance(maintenanceData.suggested_maintenance || []);
          } catch (error) {
            console.log('No maintenance data available:', error.message);
          } finally {
            setLoadingMaintenance(false);
          }
          
          setVehicleDataLoaded(true);
        };
        
        loadVehicleData();
      }
      
      // Reset when modal closes
      if (!showModal) {
        setVehicleDataLoaded(false);
        setSuggestedRepairs([]);
        setSuggestedMaintenance([]);
        setPickedParts([]);
        setPickedSuggestions({});
        setOpenDetails({});
      }
    }, [showModal, selectedJob]);
    
    if (!showModal || !diagnosisResult || !selectedJob) return null;

    const loadSuggestedRepairs = async () => {
      try {
        setLoadingRepairs(true);
        const data = await jobAPI.getSuggestedRepairs(selectedJob.id);
        setSuggestedRepairs(data.suggested_repairs || []);
      } catch (error) {
        console.error('Error loading suggested repairs:', error);
      } finally {
        setLoadingRepairs(false);
      }
    };

    const loadSuggestedMaintenance = async () => {
      try {
        setLoadingMaintenance(true);
        const data = await jobAPI.getSuggestedMaintenance(selectedJob.id);
        setSuggestedMaintenance(data.suggested_maintenance || []);
      } catch (error) {
        console.error('Error loading suggested maintenance:', error);
      } finally {
        setLoadingMaintenance(false);
      }
    };

    // The Load buttons only re-read what was collected when the job was
    // created. A job whose collection came back empty needs collecting again,
    // so the empty-state Refresh buttons do that, then reload both lists.
    const refreshVehicleData = async () => {
      setLoadingRepairs(true);
      setLoadingMaintenance(true);
      try {
        await jobAPI.collectVehicleData(selectedJob.id);
      } catch (error) {
        toast.error(
          error?.response?.data?.detail || 'Could not refresh vehicle data. Please try again.'
        );
        setLoadingRepairs(false);
        setLoadingMaintenance(false);
        return;
      }
      await Promise.all([loadSuggestedRepairs(), loadSuggestedMaintenance()]);
    };

    const toggleSuggestion = (key, suggestion, kind) => {
      setPickedSuggestions(prev => {
        const next = { ...prev };
        if (next[key]) {
          delete next[key];
        } else {
          next[key] = suggestionLine(suggestion, kind);
        }
        return next;
      });
    };

    const toggleDetails = (key) => setOpenDetails(prev => ({ ...prev, [key]: !prev[key] }));

    const estimateLines = [...pickedParts, ...Object.values(pickedSuggestions)];

    return (
      <Modal isOpen onClose={onClose} title="AI Diagnostic Results" size="xxl">
        {/* Job & Analysis Info */}
        <div className="flex items-center space-x-3 mb-2">
          <CorvinArt pose="avatar" size={40} />
          <div>
            <div className="text-sm text-ink-muted">{selectedJob.job_number} • {selectedJob.vehicle_display}</div>
            <div className="mt-1 flex items-center space-x-4">
              <span className="text-sm text-ink-muted">
                Analysis: {diagnosisResult.ai_model_version}
              </span>
              <span className="text-sm text-ink-muted">
                • {new Date(diagnosisResult.created_at).toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        <div className="space-y-8">
          {/* Confidence Score — a fallback has none to show */}
          {isFallbackDiagnosis(diagnosisResult) ? (
            <DiagnosisFallbackNotice />
          ) : (
          <div className="bg-info-tint rounded-xl p-6 border border-info-border">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-ink flex items-center">
                <Star className="h-5 w-5 mr-2 text-info" />
                Diagnostic Confidence
              </h4>
              <div className="text-right">
                <span className="text-page-title text-info">
                  {(diagnosisResult.confidence_score * 100).toFixed(1)}%
                </span>
                <div className="text-sm text-ink-muted">AI Confidence</div>
              </div>
            </div>
            <div className="w-full bg-surface-sunken rounded-full h-3">
              <div 
                className="bg-info h-3 rounded-full transition-all duration-700 ease-out"
                style={{ width: `${diagnosisResult.confidence_score * 100}%` }}
              ></div>
            </div>
            <div className="mt-2 text-sm text-ink-muted">
              Based on symptom analysis and vehicle data patterns
            </div>
          </div>
          )}

          {/* Upsell Opportunities Banner */}
          {(suggestedRepairs.length > 0 || suggestedMaintenance.length > 0) && (
            <div className="bg-success-tint rounded-card p-4 border border-success-border">
              <div className="flex items-center">
                <div className="p-2 bg-success-tint rounded-lg mr-3">
                  <TrendingUp className="h-5 w-5 text-success" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-success">Cross-Sell Opportunities Available</h4>
                  <p className="text-sm text-success">
                    Based on vehicle data, there are {suggestedRepairs.length + suggestedMaintenance.length} additional services 
                    you can recommend to this customer. Scroll down to see suggested repairs and maintenance.
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-page-title text-success">
                    {suggestedRepairs.length + suggestedMaintenance.length}
                  </span>
                  <div className="text-small text-success">opportunities</div>
                </div>
              </div>
            </div>
          )}

          {/* Enhanced Parts Section */}
          <EnhancedPartsSection
            suggestedParts={diagnosisResult.suggested_parts}
            onSelectionChange={setPickedParts}
          />

          {/* Suggested Repairs Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-ink flex items-center">
                <Wrench className="h-5 w-5 mr-2 text-warning" />
                Suggested Repairs
              </h4>
              <button
                onClick={loadSuggestedRepairs}
                disabled={loadingRepairs}
                className="flex items-center px-3 py-1 bg-warning-tint text-warning rounded hover:bg-warning-border transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${loadingRepairs ? 'animate-spin' : ''}`} />
                Load Repairs
              </button>
            </div>
              
            {loadingRepairs ? (
              <CorvinThinking size={48} label="Corvin is looking up suggested repairs." />
            ) : suggestedRepairs.length === 0 ? (
              <div className="text-center py-8 bg-warning-tint rounded-lg border border-warning-border">
                <Wrench className="h-8 w-8 text-warning mx-auto mb-2" />
                <p className="text-ink font-medium">No repair suggestions yet</p>
                <p className="text-sm text-ink-muted mt-1">
                  Vehicle data is collected automatically when the job is created.
                </p>
                <button
                  onClick={refreshVehicleData}
                  className="mt-3 px-4 py-2 bg-warning-tint text-warning rounded-lg hover:bg-warning-border transition-colors text-sm"
                >
                  Refresh Repair Data
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {suggestedRepairs.map((suggestion, index) => {
                  const repair = suggestion.repair;
                  const key = `repair-${index}`;
                  // Built as a full class string, not a template-literal color
                  // stem: tokens have no numbered shades, so `bg-${x}-100` can't
                  // resolve to `bg-danger`. 'orange' has no token equivalent
                  // (same exclusion as elsewhere in this file) and is left raw.
                  const priorityClasses = suggestion.priority === 'high'
                    ? 'bg-danger-tint text-danger'
                    : 'bg-warning-tint text-warning';

                  return (
                    <div key={index} className="border border-border-default rounded-lg p-4 transition-colors hover:border-border-strong">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h5 className="font-semibold text-ink">{repair.title || 'Unknown Repair'}</h5>
                            <span className={`inline-flex px-2 py-1 text-small font-medium rounded-full ${priorityClasses}`}>
                              {suggestion.priority} priority
                            </span>
                          </div>
                            
                          <p className="text-sm text-ink-muted mb-2">{suggestion.reason}</p>
                            
                          {openDetails[key] && repair.description && repair.description !== 'N/A' && (
                            <p className="text-sm text-ink mb-2">{repair.description}</p>
                          )}
                            
                          <div className="flex items-center space-x-4 text-sm">
                            {suggestion.average_cost && (
                              <div className="flex items-center">
                                <DollarSign className="h-4 w-4 mr-1 text-ink-muted" />
                                <span className="text-ink-muted">Avg Cost:</span>
                                <span className="ml-1 font-medium text-ink">${suggestion.average_cost}</span>
                              </div>
                            )}
                            {repair.costs?.dealer?.length > 0 && (
                              <div className="flex items-center">
                                <TrendingUp className="h-4 w-4 mr-1 text-ink-muted" />
                                <span className="text-ink-muted">Dealer Service</span>
                              </div>
                            )}
                          </div>
                        </div>
                          
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => toggleDetails(key)}
                            aria-expanded={!!openDetails[key]}
                            className="flex items-center px-3 py-1 bg-accent/20 text-accent rounded hover:bg-accent/30 transition-colors"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Details
                          </button>
                          <button
                            onClick={() => toggleSuggestion(key, suggestion, 'repair')}
                            aria-pressed={!!pickedSuggestions[key]}
                            className={`flex items-center px-3 py-1 rounded transition-colors ${
                              pickedSuggestions[key]
                                ? 'bg-success text-white hover:bg-success/80'
                                : 'bg-success-tint text-success hover:bg-success-tint/80'
                            }`}
                          >
                            {pickedSuggestions[key] ? 'Added ✓' : <><Plus className="h-4 w-4 mr-1" />Add to Job</>}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Suggested Maintenance Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-ink flex items-center">
                <Settings className="h-5 w-5 mr-2 text-accent" />
                Suggested Maintenance (Based on Mileage)
              </h4>
              <button
                onClick={loadSuggestedMaintenance}
                disabled={loadingMaintenance}
                className="flex items-center px-3 py-1 bg-accent/20 text-accent rounded hover:bg-accent/30 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${loadingMaintenance ? 'animate-spin' : ''}`} />
                Load Maintenance
              </button>
            </div>
              
            {loadingMaintenance ? (
              <CorvinThinking size={48} label="Corvin is checking the maintenance schedule." />
            ) : suggestedMaintenance.length === 0 ? (
              <div className="text-center py-8 bg-accent/10 rounded-lg border border-accent/30">
                <Settings className="h-8 w-8 text-accent mx-auto mb-2" />
                <p className="text-ink font-medium">No maintenance suggestions yet</p>
                <p className="text-sm text-ink-muted mt-1">
                  Maintenance schedules require vehicle trim data for accuracy.
                </p>
                <button
                  onClick={refreshVehicleData}
                  className="mt-3 px-4 py-2 bg-accent/20 text-accent rounded-lg hover:bg-accent/30 transition-colors text-sm"
                >
                  Refresh Maintenance Data
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {suggestedMaintenance.map((suggestion, index) => {
                  const maintenance = suggestion.maintenance;
                  const key = `maintenance-${index}`;
                  // Same full-class-string approach as the repairs list above.
                  // The 'blue' branch (default/low priority) is a static
                  // priority badge, not an interactive element — collapses to
                  // info per the plan's blue/purple role rule. 'orange' has no
                  // token equivalent and is left raw, same exclusion as elsewhere.
                  const priorityClasses = suggestion.priority === 'critical'
                    ? 'bg-danger-tint text-danger'
                    : suggestion.priority === 'high'
                    ? 'bg-warning-tint text-warning'
                    : 'bg-info-tint text-info';

                  return (
                    <div key={index} className="border border-border-default rounded-lg p-4 transition-colors hover:border-border-strong">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h5 className="font-semibold text-ink">{maintenance.title || 'Unknown Maintenance'}</h5>
                            <span className={`inline-flex px-2 py-1 text-small font-medium rounded-full ${priorityClasses}`}>
                              {suggestion.priority} priority
                            </span>
                          </div>
                            
                          <p className="text-sm text-ink-muted mb-2">{suggestion.reason}</p>
                            
                          {openDetails[key] && maintenance.description && (
                            <p className="text-sm text-ink mb-2">{maintenance.description}</p>
                          )}
                            
                          <div className="flex items-center space-x-4 text-sm">
                            <div className="flex items-center">
                              <Calendar className="h-4 w-4 mr-1 text-ink-muted" />
                              <span className="text-ink-muted">Due at:</span>
                              <span className="ml-1 font-medium text-ink">{suggestion.due_mileage.toLocaleString()} miles</span>
                            </div>
                            {suggestion.overdue_miles > 0 && (
                              <div className="flex items-center">
                                <AlertCircle className="h-4 w-4 mr-1 text-danger" />
                                <span className="text-ink-muted">Overdue by:</span>
                                <span className="ml-1 font-medium text-danger">{suggestion.overdue_miles.toLocaleString()} miles</span>
                              </div>
                            )}
                            {maintenance.service_items && maintenance.service_items.length > 1 && (
                              <div className="flex items-center">
                                <Settings className="h-4 w-4 mr-1 text-ink-muted" />
                                <span className="text-ink-muted">Multiple items</span>
                              </div>
                            )}
                          </div>
                        </div>
                          
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => toggleDetails(key)}
                            aria-expanded={!!openDetails[key]}
                            className="flex items-center px-3 py-1 bg-accent/20 text-accent rounded hover:bg-accent/30 transition-colors"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Details
                          </button>
                          <button
                            onClick={() => toggleSuggestion(key, suggestion, 'maintenance')}
                            aria-pressed={!!pickedSuggestions[key]}
                            className={`flex items-center px-3 py-1 rounded transition-colors ${
                              pickedSuggestions[key]
                                ? 'bg-success text-white hover:bg-success/80'
                                : 'bg-success-tint text-success hover:bg-success-tint/80'
                            }`}
                          >
                            {pickedSuggestions[key] ? 'Added ✓' : <><Plus className="h-4 w-4 mr-1" />Add to Job</>}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Possible Causes - Simplified */}
          <div>
            <h4 className="text-lg font-semibold text-ink mb-4 flex items-center">
              <AlertCircle className="h-5 w-5 mr-2 text-danger" />
              Likely Causes
            </h4>
            <div className="grid gap-4">
              {diagnosisResult.possible_causes?.slice(0, 3).map((cause, index) => (
                <div key={index} className="border border-border-default rounded-lg p-4 transition-colors hover:border-border-strong">
                  <div className="flex items-center justify-between">
                    <h5 className="font-semibold text-ink">{cause.cause}</h5>
                    <span className="text-lg font-bold text-accent">
                      {(cause.probability * 100).toFixed(0)}%
                    </span>
                  </div>
                  <p className="text-ink-muted text-sm mt-1">{cause.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="-mx-6 -mb-4 mt-2 px-6 py-4 border-t border-border-default bg-surface-page flex justify-between items-center">
          <div className="text-sm text-ink-muted">
            {estimateLines.length === 0
              ? 'Add parts or suggestions to build an estimate.'
              : 'Opens the invoice form to review, price and save.'}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-border-default rounded-lg text-ink hover:bg-surface-sunken transition-colors"
            >
              Close
            </button>
            <button
              onClick={() => onCreateEstimate(estimateLines)}
              disabled={estimateLines.length === 0}
              className="px-6 py-2 bg-accent text-white rounded-lg hover:bg-accent-hover transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="h-4 w-4 mr-2" />
              {estimateLines.length === 0
                ? 'Create Estimate'
                : `Create Estimate (${estimateLines.length} item${estimateLines.length === 1 ? '' : 's'})`}
            </button>
          </div>
        </div>
      </Modal>
    );
  };


export default EnhancedDiagnosisModal;
